(() => {
  if (customElements.get('reborn-impact')) return;

  customElements.define('reborn-impact', class extends HTMLElement {
    static observedAttributes = ['data-saving', 'data-carbon', 'data-score', 'data-carbon-max', 'data-demo'];

    connectedCallback() {
      // Defer until the parser has supplied this element's children.
      this.readyFrame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') this.start();
        }, options);
        this.motion.addEventListener('change', () => {
          if (this.motion.matches) this.finish();
        }, options);
        this.update();
      });
    }

    attributeChangedCallback() {
      if (this.metrics && this.isConnected) this.update();
    }

    update() {
      const numeric = (key, fallback, max = Infinity) => {
        const raw = this.dataset[key];
        const number = Number(String(raw ?? '').replace(',', '.'));
        return raw === undefined || raw.trim() === '' || !Number.isFinite(number)
          ? fallback : Math.min(max, Math.max(0, number));
      };
      const carbonMax = numeric('carbonMax', 60) || 60;
      const example = this.dataset.demo !== 'false';
      this.querySelectorAll('[data-impact-demo]').forEach(node => { node.hidden = !example; });
      // aria-label Liquid'den mağaza dilinde gelir (reborn.impact_title)
      this.metrics = [
        { key: 'saving', value: numeric('saving', 42, 100), max: 100, decimals: 0, readable: value => `%${value}` },
        { key: 'carbon', value: numeric('carbon', 28.5), max: carbonMax, decimals: 0, readable: value => `${value} kg CO₂e` },
        { key: 'score', value: numeric('score', 88, 100), max: 100, decimals: 0, readable: value => `${value} / 100` }
      ].filter(metric => this.dataset[metric.key] !== undefined && this.querySelector(`[data-reborn-impact-metric="${metric.key}"]`))
       .map(metric => {
        const row = this.querySelector(`[data-reborn-impact-metric="${metric.key}"]`);
        const format = new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
          minimumFractionDigits: metric.decimals, maximumFractionDigits: metric.decimals
        });
        row.querySelector('[data-reborn-impact-readable]').textContent =
          metric.readable(format.format(metric.value));
        return { ...metric, format, counter: row.querySelector('[data-reborn-impact-count]'),
          ring: this.querySelector(`[data-reborn-impact-ring="${metric.key}"]`) };
      });
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      if (this.motion.matches || !('IntersectionObserver' in window)) {
        this.finish();
      } else if (this.started) {
        this.start();
      } else {
        this.draw(0);
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.start();
        }, { threshold: 0.2 });
        this.observer.observe(this);
      }
    }

    draw(progress) {
      this.metrics.forEach(metric => {
        metric.counter.textContent = metric.format.format(metric.value * progress);
        const percent = Math.min(1, metric.value / metric.max) * 100 * progress;
        metric.ring.setAttribute('stroke-dashoffset', String(100 - percent));
        // Avoid a round-cap dot at zero progress.
        metric.ring.style.opacity = percent > 0 ? '1' : '0';
      });
    }

    finish() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.draw(1);
    }

    start() {
      if (!this.metrics) return;
      this.started = true;
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      if (this.motion.matches) { this.finish(); return; }
      this.draw(0);
      const began = performance.now();
      const tick = now => {
        if (!this.isConnected) return;
        const progress = Math.min(1, (now - began) / 1450);
        this.draw(1 - Math.pow(1 - progress, 3));
        if (progress < 1) this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.readyFrame);
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.events?.abort();
      this.started = false;
    }
  });
})();
